import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cashboxApi, canDoCashbox } from '../../../lib/cashboxApi.js';
import { useRole } from '../../../lib/role.tsx';
import { Icon } from '../../../components/Icon.js';

export function CashboxArchive() {
  const role = useRole();
  const qc = useQueryClient();
  const canRestore = canDoCashbox(role, 'restore');
  const canHardDelete = canDoCashbox(role, 'hard_delete');

  const { data, isLoading } = useQuery({
    queryKey: ['cashbox', 'archive'],
    queryFn: () => cashboxApi.getArchive(),
  });

  const restore = useMutation({
    mutationFn: (id: string) => cashboxApi.restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashbox'] });
    },
  });

  const hardDelete = useMutation({
    mutationFn: (id: string) => cashboxApi.hardDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashbox', 'archive'] });
      qc.invalidateQueries({ queryKey: ['cashbox', 'audit'] });
    },
  });

  const ops = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div>
      <div className="panel">
        <div className="panel-head">
          <p className="panel-title">
            Архив удалённых операций
            {total > 0 && <span className="muted"> ({total})</span>}
          </p>
          <span className="muted" style={{ fontSize: 12 }}>
            Операции хранятся 7 дней, затем удаляются безвозвратно
          </span>
        </div>

        {isLoading && (
          <p style={{ padding: 32, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>Загрузка…</p>
        )}

        {!isLoading && (
          <table className="data" data-testid="archive-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Дата операции</th>
                <th>Удалена</th>
                <th>Тип</th>
                <th>Треки</th>
                <th className="num">Сумма GEL</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ops.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>
                    Архив пуст
                  </td>
                </tr>
              )}
              {ops.map((op) => (
                <tr key={op.id}>
                  <td className="mono" style={{ fontSize: 11 }}>{op.id}</td>
                  <td className="muted">{new Date(op.operatedAt).toLocaleDateString('ru-RU')}</td>
                  <td className="muted">
                    {op.deletedAt ? new Date(op.deletedAt).toLocaleDateString('ru-RU') : '—'}
                  </td>
                  <td>
                    <span className={`cb-type cb-type-${op.type}`}>
                      {op.type === 'cash' ? '💵' : op.type === 'transfer' ? '💳' : '🇪🇺'} {op.opType}
                    </span>
                  </td>
                  <td className="muted">{op.tracks.join(', ')}</td>
                  <td className={`num tnum bold ${op.direction === 'income' ? 'cb-income' : 'cb-expense'}`}>
                    {op.direction === 'income' ? '+' : '−'}{Math.abs(Math.round(op.amountGel)).toLocaleString('ru-RU')}
                  </td>
                  <td>
                    <div className="f-row" style={{ gap: 4 }}>
                      {canRestore && (
                        <button
                          className="btn-admin sm"
                          onClick={() => restore.mutate(op.id)}
                          disabled={restore.isPending}
                          title="Восстановить"
                          data-testid={`restore-btn-${op.id}`}
                        >
                          <Icon name="arrowUp" size={12} />
                        </button>
                      )}
                      {canHardDelete && (
                        <button
                          className="btn-admin sm danger"
                          onClick={() => {
                            if (confirm('Удалить безвозвратно?')) hardDelete.mutate(op.id);
                          }}
                          disabled={hardDelete.isPending}
                          title="Удалить безвозвратно"
                        >
                          <Icon name="trash" size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
